import React, { useEffect, useState } from 'react';
import * as _ from "lodash";
import MerchantDetailsComponentProps from '../merchantDetailsComponentProps';
import Loading from 'common/ui/Loading';
import useStartup from 'common/ui/useStartup';
import impersonationService from './impersonationService';
import { AgGridColumn, AgGridReact } from '@ag-grid-community/react';
import defaultGridOptions from 'common/agGrid/defaultGridOptions';
import { formatDate } from 'common/formatters';
import { Button, FormControl, InputGroup } from 'react-bootstrap';
import AddImpersonationKeyModal from './AddImpersonationKeyModal';
import ImpersonationApiKey, { getUserIdentifierTypeName } from './impersonationApiKey';
import InformationModal, { useInformationModal } from 'common/ui/InformationModal';
import CopyToClipboard from 'common/ui/CopyToClipboard';
import ValidationError from 'common/validationError';
import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import DeleteConfirmationModal, { useDeleteConfirmationModal } from 'common/ui/DeleteConfirmationModal';


const ImpersonationSettings: React.FC<MerchantDetailsComponentProps> = props => {

    const [isLoaded, setIsLoaded] = useState(false);
    const [impersonationKeys, setImpersonationKeys] = useState<ImpersonationApiKey[]>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addingImpersonationKey, setAddingImpersonationKey] = useState<ImpersonationApiKey>(null);
    const [errors, setErrors] = useState([]);
    const [deletingKey, setDeletingKey] = useState<ImpersonationApiKey>(null);

    const createdModal = useInformationModal();
    const confirmDeleteModal = useDeleteConfirmationModal();
    
    useStartup(() => {
        loadKeys();
    });

    useEffect(() => {
        if (impersonationKeys == null) return;
        setIsLoaded(true);
    }, [impersonationKeys])

    async function loadKeys() {
        const keys = await impersonationService.listKeys(props.merchant.id);

        setImpersonationKeys(keys);
    }

    function handleAdd() {
        setAddingImpersonationKey({ id: null, name: null, notes: null, userIdentifierType: null, tokenLifetimeInSeconds: 3600, createdDate: null});
        setShowAddModal(true);
    }

    async function handleCreateKey(apiKey: ImpersonationApiKey) {

        try {
            const response = await impersonationService.createKey(props.merchant.id, apiKey);
            setImpersonationKeys([...impersonationKeys, response]);
            setShowAddModal(false);
            setErrors([]);
            createdModal.show("Impersonation Key Created", <>
                <InputGroup>
                    <FormControl value={response.secret} readOnly />
                    <InputGroup.Append>
                        <CopyToClipboard value={response.secret} />
                    </InputGroup.Append>
                </InputGroup>

                <div>
                    <strong>Important</strong>: make note of this key! For security reasons, it will never be shown again.
                </div>
            </>);
        }
        catch (error) {            
            setErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
            loggingService.error("Error creating impersonation key", error as Error);
        }
    }

    function handleDelete(apiKey: ImpersonationApiKey) {
        setDeletingKey(apiKey);
        confirmDeleteModal.show();
    }

    async function onDelete(apiKey: ImpersonationApiKey) {
        try {
            await impersonationService.deleteKey(props.merchant.id, apiKey);
            setImpersonationKeys(impersonationKeys.filter(m => m.id !== apiKey.id));
            setDeletingKey(null);
        }
        catch (error) {
            loggingService.error("Error deleting impersonation key", error as Error);
            notificationService.error("Error deleting key");
        }
    }

    if (!isLoaded) return <Loading />

    return <div className="impersonation-keys container-fluid">
        <div className="row">
            <div className="col-12 col-md-6 order-1 order-md-2">
                <h3>Instructions</h3>
                <p>
                    Impersonation API Keys are used for external systems that want to act on behalf of a specific StoneX Hedge customer. For example, a third-party app
                    that lets the user view and make offers, but doesn't use the StoneX Hedge login screen
                </p>
                <p>
                    The vendors using these keys need to be properly vetted: these keys should not be handed out lightly, given the trust needed to act on behalf of a customer.
                </p>
            </div>
            <div className="col-12 col-md-6 order-2 order-md-1">
                <div className="row table-header-row">
                    <div className="col">
                        <div className="page-sub-header">
                            Impersonation API Keys
                        </div>
                    </div>
                    <div className="col action-bar"> 
                        <Button onClick={handleAdd} variant="link">
                            <i className="material-icons">add_circle_outline</i> New Impersonation Key
                        </Button>
                    </div>
                </div>
                <div className="row">
                    <div className="ag-theme-alpine" style={{ width: "100%" }} >
                        <AgGridReact
                            {...defaultGridOptions}
                            paginationPageSize= { 25 }
                            pagination
                            rowData={impersonationKeys}
                            getRowId={params => params.data.id}
                            domLayout="autoHeight">
                            <AgGridColumn field="name" headerName="Name"
                                flex={2} />
                            <AgGridColumn field="notes" headerName="Notes"
                                flex={2} />
                            <AgGridColumn field="userIdentifierType" headerName="User Identifier"
                                valueFormatter={(params: any) => getUserIdentifierTypeName(params.value)}
                                flex={1} />
                            <AgGridColumn field="tokenLifetimeInSeconds" headerName="Token Lifetime"
                                flex={1} />
                            <AgGridColumn field="createdDate" headerName="Created"
                                valueFormatter={(params: any) => formatDate(params.value)}
                                flex={1} />
                            <AgGridColumn
                                cellRenderer={(params: any) => 
                                    <button
                                        className="link-button"
                                        onClick={() => handleDelete(params.node.data)}>
                                        <i className="material-icons">delete</i>
                                    </button>
                                }
                            />
                        </AgGridReact>
                    </div>
                </div>
            </div>
        </div>
        <AddImpersonationKeyModal
            apiKey={addingImpersonationKey}
            showModal={showAddModal}
            onClose={() => {
                setShowAddModal(false);
                setErrors([]);
            }}
            onSubmit={handleCreateKey}
            errors={errors}
            />            
        <InformationModal modalState={createdModal.state} />
        <DeleteConfirmationModal 
            controller={confirmDeleteModal}
            title="Delete API Key?"
            text={`Are you sure you want to delete this key? Any future calls using this key will fail.`}
            onConfirm={() => onDelete(deletingKey)}
            />
    </div>;
}


export default ImpersonationSettings;